import { toDateOnlyIso } from "./dataMappers";
import { attendanceRecordDateKey } from "./attendanceTimeFormat";
import { isOpenRequest } from "./requestHelpers";

const COVERING_LEAVE_STATUSES = new Set([
  "pending",
  "gm_pending",
  "in_progress",
  "approved",
]);

function isLeaveLikeRequest(request) {
  if (!request) return false;
  if (request._source === "permit" || request.category === "permit") return false;
  return (
    request._source === "leave" ||
    request.category === "leave" ||
    String(request.type || "").includes("إجازة")
  );
}

function isCoveringLeaveStatus(status) {
  const key = String(status || "").toLowerCase();
  return COVERING_LEAVE_STATUSES.has(key) || isOpenRequest({ status });
}

function addOneUtcDay(dateIso) {
  const [year, month, day] = dateIso.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return next.toISOString().slice(0, 10);
}

function eachDateInRange(startIso, endIso) {
  const start = toDateOnlyIso(startIso);
  const end = toDateOnlyIso(endIso) || start;
  if (!start || !end || start > end) return [];
  const dates = [];
  let cursor = start;
  while (cursor <= end) {
    dates.push(cursor);
    cursor = addOneUtcDay(cursor);
  }
  return dates;
}

/** Dates already covered by an open or approved leave request. */
export function leaveCoveredDateSet(requests = []) {
  const covered = new Set();
  for (const request of requests) {
    if (!isLeaveLikeRequest(request)) continue;
    if (!isCoveringLeaveStatus(request.status)) continue;
    for (const dateIso of eachDateInRange(request.startDate, request.endDate)) {
      covered.add(dateIso);
    }
  }
  return covered;
}

/**
 * Absent punch days suitable for leave suggestions.
 * Newest first. Skips days already covered by an open/approved leave.
 */
export function listUncoveredAbsentDays(records = [], existingRequests = []) {
  const covered = leaveCoveredDateSet(existingRequests);
  const byDate = new Map();

  for (const record of Array.isArray(records) ? records : []) {
    const dateIso = attendanceRecordDateKey(record?.date);
    if (!dateIso || covered.has(dateIso)) continue;
    if (String(record?.status || "").toUpperCase() !== "ABSENT") continue;
    if (!byDate.has(dateIso)) {
      byDate.set(dateIso, {
        date: dateIso,
        kind: "ABSENT",
        label: "غياب",
      });
    }
  }

  return [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));
}

export function findAbsentDayHint(absentDays, dateIso) {
  if (!dateIso) return null;
  return (absentDays || []).find((day) => day.date === dateIso) || null;
}

export function formatAbsentDayChipLabel(day) {
  if (!day?.date) return "";
  const [, month, dayOfMonth] = day.date.split("-");
  return `${Number(dayOfMonth)}/${Number(month)} · غياب`;
}

/**
 * Soft warning only for today/past leave dates without a matching absence.
 * Future dates are normal planned leave — no warning.
 */
export function shouldWarnLeaveDateWithoutAbsence(startDateIso, todayIso) {
  if (!startDateIso || !todayIso) return false;
  return startDateIso <= todayIso;
}
